/**
 * Central, typed catalog of TanStack React Query keys for the app.
 *
 * Single source of truth so that `useQuery({ queryKey: ... })` and
 * `queryClient.invalidateQueries({ queryKey: ... })` always agree — no
 * stringly-typed magic, no per-feature `*Keys.ts` files to keep in sync.
 *
 * NOT a resource registry: nothing here ties a name to CRUD paths or to a
 * "current resource" concept. Each entry is just a typed constructor that
 * produces a `readonly` array suitable as a TanStack React Query key.
 *
 * Each feature adds its own branch here as it migrates off Refine (see the
 * migration plan, vague 4).
 */
type CollaboratorsListVars = { limit: number; offset: number }
type SalesmensListVars = { limit: number; offset: number }

export const queryKeys = {
  collaborators: {
    all:  () => ['collaborators'] as const,
    list: (vars: CollaboratorsListVars) =>
      ['collaborators', 'list', vars] as const,
  },
  cadastreDepartments: {
    all:  () => ['cadastreDepartments'] as const,
    list: () => ['cadastreDepartments', 'list'] as const,
  },
  salesmens: {
    all:  () => ['salesmens'] as const,
    list: (vars: SalesmensListVars) => ['salesmens', 'list', vars] as const,
    one:  (id: number)               => ['salesmens', 'one', id] as const,
  },
  companies: {
    all:  () => ['companies'] as const,
    list: () => ['companies', 'list'] as const,
    one:  (id: number) => ['companies', 'one', id] as const,
  },
  clients: {
    all:  () => ['clients'] as const,
    list: () => ['clients', 'list'] as const,
    one:  (id: number) => ['clients', 'one', id] as const,
  },
  urbanAnalysis: {
    all:  () => ['urbanAnalysis'] as const,
    list: () => ['urbanAnalysis', 'list'] as const,
    one:  (id: number) => ['urbanAnalysis', 'one', id] as const,
  },
  projects: {
    all:  () => ['projects'] as const,
    list: (vars: {
      where: Record<string, unknown> | undefined
      order_by: Array<Record<string, unknown>>
      limit: number
      offset: number
    }) => ['projects', 'list', vars] as const,
    one:  (id: number) => ['projects', 'one', id] as const,
    countByStep: () => ['projects', 'countByStep'] as const,
    // Literal head kept stable: the project-status query and its many
    // `invalidateQueries(['project-status', id])` call sites must share a key.
    status: (id: number) => ['project-status', id] as const,
  },
} as const

export type QueryKeys = typeof queryKeys
